import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Check, CheckCircle2, Circle, Languages, Mic, Pause, Play, Plus, Square, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const LANGS = [
  ["en", "English"], ["es", "Spanish"], ["fr", "French"], ["pt", "Portuguese"], ["zh", "Mandarin"],
  ["ar", "Arabic"], ["hi", "Hindi"], ["ru", "Russian"], ["ko", "Korean"], ["vi", "Vietnamese"],
  ["tl", "Tagalog"], ["de", "German"],
];
const INFORMANTS = ["patient", "parent", "partner", "teacher", "employer", "other"];
const SEGMENT_MS = 25000;

const SPEAKER_STYLE = {
  clinician: "bg-[#1E3F20] text-white",
  ai_interviewer: "bg-[#7E9B82] text-white",
  patient: "bg-[#E3DAC9] text-[#1C1C19]",
  parent: "bg-[#D8CBB2] text-[#1C1C19]",
  partner: "bg-[#D8CBB2] text-[#1C1C19]",
  teacher: "bg-[#D8CBB2] text-[#1C1C19]",
  employer: "bg-[#D8CBB2] text-[#1C1C19]",
  other: "bg-gray-200 text-[#1C1C19]",
};
const speakerLabel = (s) => s === "ai_interviewer" ? "AI Interviewer" : s.charAt(0).toUpperCase() + s.slice(1);

export default function InterviewPage() {
  const { caseId, interviewId } = useParams();
  const navigate = useNavigate();
  const [caseInfo, setCaseInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [interviews, setInterviews] = useState(null);
  const [iv, setIv] = useState(null);
  // setup form
  const [templateSlug, setTemplateSlug] = useState("");
  const [mode, setMode] = useState("clinician");
  const [language, setLanguage] = useState("en");
  const [informant, setInformant] = useState("patient");
  const [creating, setCreating] = useState(false);
  // consent
  const [consentName, setConsentName] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  // recording
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [segStatus, setSegStatus] = useState("idle");
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const segTimerRef = useRef(null);
  const clockRef = useRef(null);
  const recordingRef = useRef(false);
  // ai mode
  const [patientText, setPatientText] = useState("");
  const [asking, setAsking] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [manualQ, setManualQ] = useState("");
  const [manualA, setManualA] = useState("");
  const [domainEditing, setDomainEditing] = useState(false);
  const [domainDraft, setDomainDraft] = useState([]);
  const [newDomain, setNewDomain] = useState("");

  useEffect(() => {
    api.get(`/clinician/cases/${caseId}`).then((r) => {
      setCaseInfo(r.data);
      const slug = r.data.product?.slug;
      api.get("/interview-templates").then((t) => {
        setTemplates(t.data);
        const match = t.data.find((x) => x.product_slug === slug);
        if (match) setTemplateSlug(match.slug);
      }).catch(() => {});
    }).catch((e) => toast.error(errMsg(e)));
    api.get(`/cases/${caseId}/interviews`).then((r) => setInterviews(r.data)).catch(() => setInterviews([]));
  }, [caseId]);

  const loadInterview = () => {
    if (!interviewId) return;
    api.get(`/interviews/${interviewId}`).then((r) => setIv(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(loadInterview, [interviewId]);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    recordingRef.current = false;
    clearTimeout(segTimerRef.current);
    clearInterval(clockRef.current);
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
  };

  const createInterview = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/interviews`, {
        template_slug: templateSlug, mode, language, informant });
      navigate(`/clinician/cases/${caseId}/interview/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCreating(false); }
  };

  const giveConsent = async () => {
    try {
      await api.post(`/interviews/${interviewId}/consent`, { agreed: consentChecked, by_name: consentName });
      toast.success("Consent recorded. Interview started.");
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendSegment = async (blob) => {
    if (blob.size < 800) return;
    setSegStatus("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "segment.webm");
      const { data } = await api.post(`/interviews/${interviewId}/audio`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (data.turns?.length) {
        setIv((prev) => ({ ...prev, turns: [...(prev?.turns || []), ...data.turns] }));
      }
    } catch (e) { toast.error(errMsg(e)); }
    setSegStatus("idle");
  };

  const beginSegment = () => {
    if (!recordingRef.current || !streamRef.current) return;
    const rec = new MediaRecorder(streamRef.current);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      sendSegment(blob);
      if (recordingRef.current) beginSegment();
    };
    rec.start();
    recorderRef.current = rec;
    segTimerRef.current = setTimeout(() => { try { rec.stop(); } catch { /* ignore */ } }, SEGMENT_MS);
  };

  const startRecording = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingRef.current = true;
      setRecording(true); setPaused(false); setSeconds(0);
      clockRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      beginSegment();
    } catch {
      toast.error("Microphone access denied or unavailable.");
    }
  };

  const pauseRecording = () => {
    if (paused) {
      try { recorderRef.current?.resume(); } catch { /* ignore */ }
      segTimerRef.current = setTimeout(() => { try { recorderRef.current?.stop(); } catch { /* ignore */ } }, SEGMENT_MS);
      setPaused(false);
    } else {
      clearTimeout(segTimerRef.current);
      try { recorderRef.current?.pause(); } catch { /* ignore */ }
      setPaused(true);
    }
  };

  const stopRecording = () => {
    recordingRef.current = false;
    clearTimeout(segTimerRef.current);
    clearInterval(clockRef.current);
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    setRecording(false); setPaused(false);
  };

  const submitAnswer = async () => {
    if (!patientText.trim()) return;
    setAsking(true);
    try {
      await api.post(`/interviews/${interviewId}/turns`, { speaker: iv.informant || "patient", text: patientText.trim() });
      setPatientText("");
      const { data } = await api.post(`/interviews/${interviewId}/next-question`);
      if (data.command === "end") toast.info("Interviewer suggests the interview is complete.");
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); } finally { setAsking(false); }
  };

  const sendInstruction = async () => {
    if (!instruction.trim()) return;
    try {
      await api.post(`/interviews/${interviewId}/override`, { instruction: instruction.trim() });
      setInstruction("");
      toast.success("Instruction recorded — the interviewer will honor it.");
      if (iv.mode === "ai") {
        setAsking(true);
        try { await api.post(`/interviews/${interviewId}/next-question`); } catch { /* ignore */ } finally { setAsking(false); }
      }
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addExchange = async () => {
    if (!manualA.trim()) return;
    setBusy(true);
    try {
      if (manualQ.trim()) await api.post(`/interviews/${interviewId}/turns`, { speaker: "clinician", text: manualQ.trim() });
      await api.post(`/interviews/${interviewId}/turns`, { speaker: iv.informant || "patient", text: manualA.trim() });
      setManualQ("");
      setManualA("");
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveDomains = async () => {
    try {
      await api.put(`/interviews/${interviewId}/domains`, { domains: domainDraft });
      toast.success("Interview sections updated.");
      setDomainEditing(false);
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const flipSpeaker = async (index, current) => {    const next = current === "clinician" ? (iv.informant || "patient") : "clinician";
    try {
      await api.post(`/interviews/${interviewId}/turns/${index}/speaker`, { speaker: next });
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const extractNow = async () => {
    setBusy(true);
    try {
      await api.post(`/interviews/${interviewId}/extract`);
      toast.success("Findings extracted — review below.");
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const completeInterview = async () => {
    setBusy(true);
    try {
      await api.post(`/interviews/${interviewId}/complete`);
      toast.success("Interview completed. Review the findings.");
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const reviewFinding = async (findingId, action, content) => {
    try {
      await api.post(`/interviews/${interviewId}/findings/${findingId}/review`, { action, content });
      loadInterview();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyToReport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/interviews/${interviewId}/apply`);
      toast.success(`Applied to ${data.applied.length} report section(s).`);
      navigate(`/clinician/cases/${caseId}/report`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const readAloud = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = iv?.language || "en";
      speechSynthesis.speak(u);
    } catch { /* unsupported */ }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const domainStatus = useMemo(() => {
    const st = iv?.state || {};
    const map = {};
    (st.completed_domains || []).forEach((d) => { map[d] = "done"; });
    (st.partially_completed_domains || []).forEach((d) => { map[d] = map[d] || "partial"; });
    return map;
  }, [iv]);

  // ---------------- setup / list view ----------------
  if (!interviewId) {
    return (
      <DashboardShell title="Clinical interview" subtitle={`${caseInfo?.client?.name || ""} · ${caseInfo?.product?.name || ""}`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl">
          <div className="card-warm p-6 space-y-4 h-fit" data-testid="interview-setup">
            <p className="font-heading text-xl font-semibold">New interview</p>
            <div className="space-y-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Interview protocol</p>
              <Select value={templateSlug} onValueChange={setTemplateSlug}>
                <SelectTrigger data-testid="interview-template-select" className="border-[#E5E5DF]"><SelectValue placeholder="Select protocol" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name} v{t.version}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Mode</p>
              <div className="grid grid-cols-1 gap-2">
                {[["clinician", "Clinician-conducted — you lead; the system records, transcribes, and structures"],
                  ["manual", "Self-written — you write each question and answer yourself (AI extraction still available)"],
                  ["ai", "AI-assisted — the interviewer asks adaptive questions and you supervise"]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setMode(v)} data-testid={`mode-${v}`}
                    className={`text-left p-3 rounded-lg border text-sm transition-colors ${mode === v ? "border-[#1E3F20] bg-[#1E3F20]/5 font-semibold" : "border-[#E5E5DF] hover:border-[#7E9B82]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Language</p>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="interview-language-select" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Informant</p>
                <Select value={informant} onValueChange={setInformant}>
                  <SelectTrigger data-testid="interview-informant-select" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
                  <SelectContent>{INFORMANTS.map((i) => <SelectItem key={i} value={i}>{speakerLabel(i)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={createInterview} disabled={creating || !templateSlug} data-testid="create-interview-btn"
              className="w-full rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
              {creating ? "Creating…" : "Create interview"}
            </Button>
          </div>
          <div className="lg:col-span-2 space-y-3" data-testid="interview-list">
            <p className="label-caps text-[#7E9B82]">Previous interviews</p>
            {interviews === null ? (
              <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" />
            ) : interviews.length === 0 ? (
              <div className="card-warm p-10 text-center"><p className="text-sm text-[#5C5C57]">No interviews yet for this case.</p></div>
            ) : interviews.map((i) => (
              <button key={i.id} type="button" onClick={() => navigate(`/clinician/cases/${caseId}/interview/${i.id}`)}
                className="w-full text-left card-warm px-6 py-4 hover:-translate-y-0.5 transition-transform" data-testid={`interview-row-${i.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{i.template_name} v{i.template_version}</p>
                  <span className="text-xs font-bold text-[#1E3F20] bg-[#E3DAC9]/60 rounded-full px-3 py-1 capitalize">{i.status}</span>
                </div>
                <p className="text-xs text-[#5C5C57] mt-1">
                  {i.mode === "ai" ? "AI-assisted" : "Clinician-conducted"} · {LANGS.find(([v]) => v === i.language)?.[1] || i.language} ·
                  {" "}{i.turn_count} turns · {i.finding_count} findings · {new Date(i.created_at).toLocaleString()}
                </p>
              </button>
            ))}
          </div>
        </div>
      </DashboardShell>
    );
  }

  if (!iv) {
    return <DashboardShell title="Interview"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;
  }

  // ---------------- consent view ----------------
  if (iv.status === "setup") {
    return (
      <DashboardShell title="Recording consent" subtitle={`${iv.template_name} v${iv.template_version} · ${iv.mode === "ai" ? "AI-assisted" : "Clinician-conducted"}`}>
        <div className="card-warm p-8 max-w-2xl space-y-5" data-testid="consent-panel">
          <p className="text-sm leading-7">
            Before recording begins, confirm that the participant has been informed that this interview will be
            <span className="font-semibold"> recorded for transcription purposes</span>, that audio is processed securely and
            <span className="font-semibold"> not retained after transcription</span>, that transcripts and structured findings
            become part of the confidential evaluation record, and that an AI system assists with transcription, translation,
            and information organization under clinician supervision.
          </p>
          <p className="text-xs text-[#5C5C57]">Recording-consent requirements vary by jurisdiction; follow your state's law and your practice's consent policy.</p>
          <div className="space-y-2">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Consent provided by (name)</p>
            <Input value={consentName} onChange={(e) => setConsentName(e.target.value)} data-testid="consent-name-input"
              className="input-warm border-[#E5E5DF]" placeholder="Participant or legal representative" />
          </div>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox checked={consentChecked} onCheckedChange={(v) => setConsentChecked(!!v)} data-testid="consent-checkbox" className="mt-0.5" />
            I confirm the participant has been informed and consents to recording and AI-assisted transcription.
          </label>
          <Button onClick={giveConsent} disabled={!consentChecked || !consentName.trim()} data-testid="consent-submit-btn"
            className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
            Record consent & begin interview
          </Button>
        </div>
      </DashboardShell>
    );
  }

  const inProgress = iv.status === "in_progress";
  const acceptedCount = (iv.findings || []).filter((f) => f.status === "accepted").length;
  const templateDomains = iv.template?.domains || [];

  // ---------------- session / review view ----------------
  return (
    <DashboardShell title={iv.template_name}
      subtitle={`v${iv.template_version} · ${iv.mode === "ai" ? "AI-assisted" : "Clinician-conducted"} · ${LANGS.find(([v]) => v === iv.language)?.[1]} · ${speakerLabel(iv.informant)} · ${iv.status}`}
      actions={inProgress && (
        <div className="flex gap-3">
          <Button variant="outline" onClick={extractNow} disabled={busy || !(iv.turns || []).length} data-testid="extract-findings-btn"
            className="rounded-full border-[#1E3F20]/30 text-[#1E3F20] hover:bg-[#E3DAC9]/40">Extract findings</Button>
          <Button onClick={completeInterview} disabled={busy} data-testid="complete-interview-btn"
            className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
            {busy ? "Working…" : "Complete interview"}
          </Button>
        </div>)}>
      {(iv.safety_flags || []).length > 0 && (
        <div className="rounded-xl border border-[#8C2A2A]/40 bg-[#8C2A2A]/5 p-5 mb-6 max-w-5xl" data-testid="safety-flags-panel">
          <p className="flex items-center gap-2 font-semibold text-[#8C2A2A]"><AlertTriangle className="h-4 w-4" /> Safety review required</p>
          <ul className="mt-2 space-y-1 text-sm text-[#8C2A2A]">
            {iv.safety_flags.map((f, i) => <li key={i} data-testid={`safety-flag-${i}`}>• {f}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-7xl">
        {/* progress sidebar */}
        <aside className="card-warm p-5 h-fit lg:sticky lg:top-24" data-testid="domain-progress">
          <p className="label-caps text-[#7E9B82] mb-4">Interview progress</p>
          <div className="space-y-2">
            {templateDomains.map((d) => {
              const st = domainStatus[d.id];
              return (
                <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`domain-${d.id}`}>
                  {st === "done" ? <CheckCircle2 className="h-4 w-4 text-[#2C5A2E] shrink-0" />
                    : st === "partial" ? <Circle className="h-4 w-4 text-[#B8860B] shrink-0" />
                    : <Circle className="h-4 w-4 text-[#C9C9C0] shrink-0" />}
                  <span className={st ? "font-medium" : "text-[#5C5C57]"}>{d.label}</span>
                </div>
              );
            })}
          </div>
          {(iv.status === "setup" || inProgress) && (
            <div className="mt-4">
              {!domainEditing ? (
                <button type="button" onClick={() => { setDomainDraft(templateDomains.map((d) => ({ ...d }))); setDomainEditing(true); }}
                  data-testid="customize-domains-btn"
                  className="text-xs font-semibold text-[#1E3F20] hover:underline">
                  Customize sections…
                </button>
              ) : (
                <div className="space-y-2" data-testid="domain-editor">
                  {domainDraft.map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Input value={d.label} data-testid={`domain-edit-${i}`}
                        onChange={(e) => setDomainDraft((dd) => dd.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                        className="input-warm border-[#E5E5DF] text-xs h-8" />
                      <button type="button" onClick={() => setDomainDraft((dd) => dd.filter((_, j) => j !== i))}
                        data-testid={`domain-remove-${i}`} className="text-[#8C2A2A] shrink-0"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="New section…"
                      data-testid="domain-add-input" className="input-warm border-[#E5E5DF] text-xs h-8" />
                    <button type="button" data-testid="domain-add-btn"
                      onClick={() => {
                        if (!newDomain.trim()) return;
                        setDomainDraft((dd) => [...dd, { id: newDomain.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_"), label: newDomain.trim() }]);
                        setNewDomain("");
                      }}
                      className="text-[#1E3F20] shrink-0"><Plus className="h-4 w-4" /></button>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={saveDomains} data-testid="domains-save-btn"
                      className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white h-7 text-xs">Save sections</Button>
                    <Button size="sm" variant="ghost" onClick={() => setDomainEditing(false)} data-testid="domains-cancel-btn"
                      className="rounded-full h-7 text-xs">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {(iv.state?.contradictions || []).length > 0 && (
            <div className="mt-4 rounded-lg bg-[#8C2A2A]/5 border border-[#8C2A2A]/30 p-3">
              <p className="text-xs font-bold text-[#8C2A2A] mb-1">Contradictions noted</p>
              {iv.state.contradictions.map((c, i) => <p key={i} className="text-xs text-[#8C2A2A]">• {c}</p>)}
            </div>
          )}
          {inProgress && (
            <div className="mt-5 space-y-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Clinician override</p>
              <div className="flex gap-2">
                <Input value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="override-input"
                  placeholder='e.g., "Skip family history"' className="input-warm border-[#E5E5DF] text-xs" />
                <Button size="sm" variant="outline" onClick={sendInstruction} data-testid="override-send-btn"
                  className="rounded-full border-[#1E3F20]/30 shrink-0"><ArrowRight className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          )}
        </aside>

        <div className="lg:col-span-3 space-y-6">
          {/* controls */}
          {inProgress && (
            <div className="card-warm p-6 flex flex-wrap items-center gap-4" data-testid="recording-controls">
              {!recording ? (
                <Button onClick={startRecording} data-testid="start-recording-btn"
                  className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                  <Mic className="h-4 w-4 mr-2" /> Start recording
                </Button>
              ) : (
                <>
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#8C2A2A]" data-testid="recording-indicator">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#8C2A2A] animate-pulse" />
                    {paused ? "Paused" : "Recording"} {fmt(seconds)}
                  </span>
                  <Button variant="outline" onClick={pauseRecording} data-testid="pause-recording-btn" className="rounded-full border-[#1E3F20]/30">
                    {paused ? <><Play className="h-4 w-4 mr-1.5" /> Resume</> : <><Pause className="h-4 w-4 mr-1.5" /> Pause</>}
                  </Button>
                  <Button variant="outline" onClick={stopRecording} data-testid="stop-recording-btn"
                    className="rounded-full border-[#8C2A2A]/40 text-[#8C2A2A] hover:bg-red-50">
                    <Square className="h-3.5 w-3.5 mr-1.5" /> Stop
                  </Button>
                </>
              )}
              {segStatus === "transcribing" && <span className="text-xs text-[#5C5C57]" data-testid="segment-status">Transcribing segment…</span>}
              <span className="text-xs text-[#5C5C57] ml-auto">Audio is transcribed in ~25-second segments and not retained. {iv.mode !== "clinician" && "Recording is optional in this mode — it captures the conversation for transcription alongside your written/AI-assisted flow."}</span>
            </div>
          )}

          {inProgress && iv.mode === "ai" && (
            <div className="card-warm p-6 space-y-4" data-testid="ai-interview-controls">
              {(iv.turns || []).length === 0 && (
                <div className="rounded-lg bg-[#E3DAC9]/40 p-4 text-sm flex items-start justify-between gap-3" data-testid="opening-question">
                  <p><span className="font-semibold">AI Interviewer: </span>{iv.template?.opening_question}</p>
                  <Button size="sm" variant="ghost" onClick={() => readAloud(iv.template?.opening_question || "")} data-testid="read-opening-btn" className="shrink-0 rounded-full"><Volume2 className="h-4 w-4" /></Button>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">{speakerLabel(iv.informant)} response (type or paste — or use clinician recording mode for live speech)</p>
                <Textarea rows={3} value={patientText} onChange={(e) => setPatientText(e.target.value)}
                  data-testid="patient-response-input" className="input-warm border-[#E5E5DF] text-sm"
                  placeholder="Type the response exactly as given…" />
              </div>
              <div className="flex gap-2">
                <Button onClick={submitAnswer} disabled={asking || !patientText.trim()} data-testid="submit-response-btn"
                  className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                  {asking ? "Thinking…" : "Submit response → next question"}
                </Button>
              </div>
            </div>
          )}

          {inProgress && iv.mode === "manual" && (
            <div className="card-warm p-6 space-y-4" data-testid="manual-interview-controls">
              <div className="space-y-2">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Your question</p>
                <Textarea rows={2} value={manualQ} onChange={(e) => setManualQ(e.target.value)}
                  data-testid="manual-question-input" className="input-warm border-[#E5E5DF] text-sm"
                  placeholder="Write the interview question…" />
              </div>
              <div className="space-y-2">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">{speakerLabel(iv.informant)} answer</p>
                <Textarea rows={3} value={manualA} onChange={(e) => setManualA(e.target.value)}
                  data-testid="manual-answer-input" className="input-warm border-[#E5E5DF] text-sm"
                  placeholder="Write the response exactly as given…" />
              </div>
              <Button onClick={addExchange} disabled={busy || !manualA.trim()} data-testid="add-exchange-btn"
                className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                Add exchange to transcript
              </Button>
            </div>
          )}

          {/* transcript */}
          <section className="card-warm p-6" data-testid="transcript-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold">Transcript</h2>
              {iv.language !== "en" && (
                <span className="flex items-center gap-1.5 text-xs text-[#5C5C57]"><Languages className="h-3.5 w-3.5" /> Original + English shown</span>
              )}
            </div>
            {(iv.turns || []).length === 0 ? (
              <p className="text-sm text-[#5C5C57]">No transcript yet. {iv.mode === "clinician" ? "Start recording to capture the conversation." : "Submit the first response."}</p>
            ) : (
              <div className="space-y-3 max-h-[480px] overflow-y-auto pr-2">
                {iv.turns.map((t, i) => (
                  <div key={i} className="flex flex-col gap-1" data-testid={`turn-${i}`}>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => inProgress && flipSpeaker(i, t.speaker)}
                        title={inProgress ? "Click to correct speaker" : undefined}
                        data-testid={`turn-speaker-${i}`}
                        className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${SPEAKER_STYLE[t.speaker] || SPEAKER_STYLE.other}`}>
                        {speakerLabel(t.speaker)}
                      </button>
                    </div>
                    <p className="text-sm leading-6 pl-1">{t.text}</p>
                    {t.text_original && t.text_original !== t.text && (
                      <p className="text-xs text-[#5C5C57] italic pl-1 border-l-2 border-[#E3DAC9] ml-1">Original: {t.text_original}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* findings review */}
          {(iv.findings || []).length > 0 && (
            <section className="card-warm p-6" data-testid="findings-panel">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-heading text-xl font-semibold">Structured findings — clinician review</h2>
                {(iv.status === "completed" || iv.status === "applied") && (
                  <Button onClick={applyToReport} disabled={busy || acceptedCount === 0} data-testid="apply-to-report-btn"
                    className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors" size="sm">
                    Apply {acceptedCount} accepted finding{acceptedCount === 1 ? "" : "s"} to report
                  </Button>
                )}
              </div>
              <p className="text-xs text-[#5C5C57] mb-4">Every statement traces to the transcript. Accept, edit, or reject — nothing enters the report without your approval.</p>
              <div className="space-y-4">
                {iv.findings.map((f) => (
                  <FindingCard key={f.finding_id} finding={f} onReview={reviewFinding} readOnly={iv.status === "applied"} />
                ))}
              </div>
            </section>
          )}

          {iv.evidence_summary && (
            <section className="card-warm p-6" data-testid="evidence-summary">
              <h2 className="font-heading text-xl font-semibold mb-3">Evidence summary</h2>
              <p className="text-sm leading-7 whitespace-pre-wrap">{iv.evidence_summary}</p>
            </section>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function FindingCard({ finding, onReview, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(finding.content);
  const badge = finding.status === "accepted" ? "bg-[#2C5A2E]/10 text-[#2C5A2E]"
    : finding.status === "rejected" ? "bg-red-100 text-red-900" : "bg-[#E3DAC9]/60 text-[#5C5C57]";
  return (
    <div className={`rounded-lg border p-4 ${finding.status === "rejected" ? "border-[#E5E5DF] opacity-60" : "border-[#E5E5DF]"}`}
      data-testid={`finding-${finding.finding_id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#1E3F20]/5 text-[#1E3F20]">{finding.domain.replace(/_/g, " ")}</span>
        <span className="text-[11px] text-[#5C5C57]">{finding.source?.replace(/_/g, " ")} · {Math.round((finding.confidence || 0) * 100)}%{finding.time_period ? ` · ${finding.time_period}` : ""}</span>
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ml-auto ${badge}`} data-testid={`finding-status-${finding.finding_id}`}>{finding.status}</span>
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`finding-edit-${finding.finding_id}`} className="input-warm border-[#E5E5DF] text-sm" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { onReview(finding.finding_id, "edit", draft); setEditing(false); }} data-testid={`finding-save-${finding.finding_id}`}
              className="rounded-full bg-[#1E3F20] text-white"><Check className="h-3.5 w-3.5 mr-1" /> Save & accept</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(finding.content); }} className="rounded-full">Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="text-sm leading-6">{finding.content}</p>
      )}
      {finding.evidence_text && (
        <p className="text-xs text-[#5C5C57] italic mt-2 border-l-2 border-[#E3DAC9] pl-3" data-testid={`finding-evidence-${finding.finding_id}`}>
          Evidence: "{finding.evidence_text}"
        </p>
      )}
      {!readOnly && !editing && (
        <div className="flex gap-2 mt-3">
          {finding.status !== "accepted" && (
            <Button size="sm" variant="outline" onClick={() => onReview(finding.finding_id, "accept")} data-testid={`finding-accept-${finding.finding_id}`}
              className="rounded-full border-[#2C5A2E]/40 text-[#2C5A2E] hover:bg-green-50 h-7 text-xs"><Check className="h-3 w-3 mr-1" /> Accept</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`finding-edit-btn-${finding.finding_id}`}
            className="rounded-full border-[#1E3F20]/30 text-[#1E3F20] h-7 text-xs">Edit</Button>
          {finding.status !== "rejected" && (
            <Button size="sm" variant="outline" onClick={() => onReview(finding.finding_id, "reject")} data-testid={`finding-reject-${finding.finding_id}`}
              className="rounded-full border-[#8C2A2A]/40 text-[#8C2A2A] hover:bg-red-50 h-7 text-xs"><X className="h-3 w-3 mr-1" /> Reject</Button>
          )}
        </div>
      )}
    </div>
  );
}
